import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useStableAvatar } from "@/hooks/use-stable-avatar";

interface ReadingListItem {
  id: string;
  book_id: number;
  profile_id: string;
  added_at: string;
}

export function useReadingList() {
  const queryClient = useQueryClient();
  const { currentProfile } = useStableAvatar();

  // Fetch user's reading list for current profile
  const { data: readingList = [], isLoading } = useQuery({
    queryKey: ['reading-list', currentProfile?.id],
    queryFn: async () => {
      if (!currentProfile?.id) return [];
      
      const { data, error } = await supabase
        .from('user_reading_list')
        .select('*')
        .eq('profile_id', currentProfile.id)
        .order('added_at', { ascending: false });

      if (error) throw error;
      return data as ReadingListItem[];
    },
    enabled: !!currentProfile?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes - list doesn't change often
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on every focus
    refetchOnMount: false, // Use cached data
  });

  // Add book to reading list with optimistic updates
  const addToReadingListMutation = useMutation({
    mutationFn: async (bookId: number) => {
      if (!currentProfile?.id) throw new Error('No profile selected');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('user_reading_list')
        .insert({ 
          book_id: bookId, 
          user_id: user.id,
          profile_id: currentProfile.id
        });

      if (error) {
        throw error;
      }
      
      return bookId;
    },
    onMutate: async (bookId: number) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['reading-list', currentProfile?.id] });

      // Snapshot the previous value
      const previousReadingList = queryClient.getQueryData(['reading-list', currentProfile?.id]) as ReadingListItem[] || [];

      // Optimistically update to the new value
      const newItem: ReadingListItem = {
        id: `temp-${bookId}-${Date.now()}`,
        book_id: bookId,
        profile_id: currentProfile?.id || '',
        added_at: new Date().toISOString()
      };
      
      queryClient.setQueryData(['reading-list', currentProfile?.id], [...previousReadingList, newItem]);

      return { previousReadingList };
    },
    onError: (error: any, bookId: number, context: any) => {
      // Rollback optimistic update on error
      if (context?.previousReadingList) {
        queryClient.setQueryData(['reading-list', currentProfile?.id], context.previousReadingList);
      }
      
      toast({
        title: "Error",
        description: "Failed to add book to reading list.",
        variant: "destructive",
      });
    },
    onSuccess: (bookId: number) => {
      // Invalidate and refetch to get the real data from server
      queryClient.invalidateQueries({ queryKey: ['reading-list', currentProfile?.id] });
      toast({
        title: "Added to Reading List",
        description: "Book has been added to your reading list.",
      });
    }
  });

  // Remove book from reading list with optimistic updates
  const removeFromReadingListMutation = useMutation({
    mutationFn: async (bookId: number) => {
      if (!currentProfile?.id) throw new Error('No profile selected');
      
      const { error } = await supabase
        .from('user_reading_list')
        .delete()
        .eq('book_id', bookId)
        .eq('profile_id', currentProfile.id);

      if (error) throw error;
      return bookId;
    },
    onMutate: async (bookId: number) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['reading-list', currentProfile?.id] });

      // Snapshot the previous value
      const previousReadingList = queryClient.getQueryData(['reading-list', currentProfile?.id]) as ReadingListItem[] || [];

      // Optimistically update to the new value
      const updatedList = previousReadingList.filter(item => item.book_id !== bookId);
      queryClient.setQueryData(['reading-list', currentProfile?.id], updatedList);

      return { previousReadingList };
    },
    onError: (error: any, bookId: number, context: any) => {
      // Rollback optimistic update on error
      if (context?.previousReadingList) {
        queryClient.setQueryData(['reading-list', currentProfile?.id], context.previousReadingList);
      }
      
      toast({
        title: "Error",
        description: "Failed to remove book from reading list.",
        variant: "destructive",
      });
    },
    onSuccess: (bookId: number) => {
      // Invalidate and refetch to get the real data from server
      queryClient.invalidateQueries({ queryKey: ['reading-list', currentProfile?.id] });
      toast({
        title: "Removed from Reading List",
        description: "Book has been removed from your reading list.",
      });
    }
  });

  // Check if book is in reading list
  const isBookInReadingList = (bookId: number) => {
    const isInList = readingList.some(item => item.book_id === bookId);
    return isInList;
  };

  // Get book IDs from reading list
  const readingListBookIds = readingList.map(item => item.book_id);

  return {
    readingList,
    readingListBookIds,
    isLoading,
    isBookInReadingList,
    addToReadingList: addToReadingListMutation.mutate,
    removeFromReadingList: removeFromReadingListMutation.mutate,
    isAdding: addToReadingListMutation.isPending,
    isRemoving: removeFromReadingListMutation.isPending,
  };
}